import { useState, useRef, useEffect } from 'react'
import cutileiApi from 'services/cutileiApi'
import BankingService from 'services/Banking'
import MoneyInput from 'components/Inputs/MoneyInput'
import TextInput from 'components/Inputs/TextInput'
import DateInput from 'components/Inputs/DateInput'
import AlertDialog from 'components/AlertDialog'
import MainButton from 'components/Buttons/MainButton'
import SecondaryButton from 'components/Buttons/SecondaryButton'
import bankingPixQrCodeValidator from 'validators/bankingPixQrCodeValidator'
import { Formik } from 'formik'
import { DateTime } from 'luxon'
import { MdQrCode2 } from 'react-icons/md'

import {
  Form,
  Row,
  FormField,
  FieldLabel,
  FormActions,
  HighlightRow,
  CardHeader,
  CardTitle,
  Caption,
  DataList,
  DataRow,
  DataLabel,
  DataValue,
  StatusBadge,
  Divider,
  WarningText,
  InfoText,
  MoneyTextMask,
  PlainMoneyText
} from '../../styles'

const formatDate = date => DateTime.fromISO (date).toFormat ('dd/MM/yyyy')

const formatMoney = value => Number (value).toLocaleString ('pt-BR', {
  style: 'currency',
  currency: 'BRL'
})

// Agendamento só faz sentido para hoje em diante.
const isPastDate = ({ date }) => (
  DateTime.fromJSDate (date).startOf ('day') < DateTime.now ().startOf ('day')
)

/*
  Pix por copia e cola.

  Diferente do `PixForm`, este formulário tem duas etapas — e é isso que muda tudo. O Pix
  por chave não consegue mostrar quem vai receber porque a Asaas não resolve chave de
  terceiro pelo DICT; o código copia e cola carrega o favorecido dentro dele, e a leitura na
  API devolve nome, instituição e documento antes de qualquer confirmação.

  Não há leitura de QR Code aqui: a câmera é dos aplicativos. No site, o caminho é colar o
  código, que é como ele chega — por mensagem, por e-mail, por boleto digital.
*/
/*
  A mesma espera dos campos de busca do site: o texto aparece na hora e só a consulta é
  adiada, para que colar não dispare uma chamada por caractere.
*/
const DECODE_DEBOUNCE_MS = 300

/*
  O que a tela diz enquanto decide sozinha. Sem botão, o silêncio seria o pior desfecho:
  quem colou um código pela metade precisa saber que faltou pedaço, e não ficar esperando.
*/
const renderCodeHint = (payload, reading) => {
  const value = (payload ?? '').trim ()

  if (reading) return 'Consultando…'
  if (value === '') return 'Cole o código e a consulta acontece sozinha'
  if (BankingService.isPixPayload (value)) return 'Código completo. Consultando…'

  return BankingService.looksLikePixPayload (value)
    ? 'Este código Pix parece estar incompleto. Copie e cole o código inteiro'
    : 'Este não é um código Pix válido'
}

export default function PixQrForm ({
  availableBalance,
  requestConfig,
  onCompleted = () => {}
}) {
  const [decoded, setDecoded] = useState (null)
  const [paying, setPaying] = useState (false)
  const [receipt, setReceipt] = useState (null)
  const [showConfirmDialog, setShowConfirmDialog] = useState (false)
  const [showOkDialog, setShowOkDialog] = useState (false)
  const [requestError, setRequestError] = useState (null)

  /*
    A chave de idempotência protege uma tentativa de pagamento inteira, e não uma
    requisição. Ela importa mais aqui do que nos outros fluxos: o `POST /pix/qrCodes/pay` do
    Asaas não aceita referência nossa, então é esta chave que impede um reenvio de virar um
    segundo pagamento.
  */
  const idempotencyKey = useRef (BankingService.newIdempotencyKey ())

  /* Guarda a leitura agendada, para cancelá-la a cada tecla e ao sair da tela. */
  const decodeTimer = useRef (null)

  useEffect (() => () => clearTimeout (decodeTimer.current), [])

  const toggleConfirmDialog = () => setShowConfirmDialog (prev => !prev)
  const toggleOkDialog = () => setShowOkDialog (prev => !prev)

  const total = decoded
    ? Number (decoded.totalValue ?? decoded.value ?? 0) + Number (decoded.fee ?? 0)
    : 0

  const canChangeValue = Boolean (decoded?.canBePaidWithDifferentValue)

  const payQrCode = async (values, resetForm) => {
    try {
      setPaying (true)
      setRequestError (null)

      const { data: transaction } = await cutileiApi.post ('/banking_account/pix_qr_codes', {
        payload: values.payload.trim (),
        /*
          O valor só vai quando o próprio QR Code admite outro. Num código de valor fixo
          quem manda é a leitura que o servidor refaz na hora de pagar, com os juros e o
          desconto do dia — mandar daqui o número que a tela mostrou seria mandar um valor
          velho.
        */
        ...canChangeValue && values.value > 0 && {value: values.value},
        ...values.scheduleDate && {scheduleDate: values.scheduleDate},
        ...values.description && {description: values.description}
      }, {
        ...requestConfig,
        headers: {
          ...requestConfig?.headers,
          'X-Idempotency-Key': idempotencyKey.current
        }
      })

      setReceipt ({...transaction, receiverName: decoded?.receiver?.name})
      setDecoded (null)
      // Limpa o código junto: um Pix pago não pode ser repetido por engano.
      resetForm ()
      idempotencyKey.current = BankingService.newIdempotencyKey ()
      toggleOkDialog ()
      onCompleted ()
    } catch (error) {
      console.log (error.response ?? error)

      /*
        Falha sem resposta é o caso que a chave existe para cobrir: não dá para saber se o
        pagamento aconteceu, e a repetição precisa ir com a mesma chave para a API devolver
        a transação que já existe. Com resposta, a tentativa seguinte é um pagamento novo.
      */
      if (!BankingService.shouldKeepIdempotencyKey (error)) {
        idempotencyKey.current = BankingService.newIdempotencyKey ()
      }

      setRequestError (error.response?.data?.message ?? 'Não foi possível pagar o Pix')
    } finally {
      setPaying (false)
    }
  }

  return (
    <Formik
      validationSchema={bankingPixQrCodeValidator}
      enableReinitialize
      initialValues={{
        availableBalance,
        payload: '',
        value: 0,
        description: '',
        scheduleDate: null
      }}
      /*
        O `onSubmit` é a leitura, não o pagamento: com o código válido, consulta o
        favorecido e o valor. Pagar só acontece depois, pelo diálogo de confirmação.
      */
      onSubmit={async (values, { setSubmitting }) => {
        try {
          setSubmitting (true)
          setRequestError (null)

          const { data } = await cutileiApi.post ('/banking_account/pix_qr_codes/decode', {
            payload: values.payload.trim (),
            ...values.scheduleDate && {expectedPaymentDate: values.scheduleDate}
          }, requestConfig)

          setDecoded (data)
          setReceipt (null)
        } catch (error) {
          console.log (error.response ?? error)
          setDecoded (null)
          setRequestError (error.response?.data?.message ?? 'Não foi possível ler o código Pix')
        } finally {
          setSubmitting (false)
        }
      }}
    >
      {({
        values,
        errors,
        touched,
        isSubmitting,
        setFieldValue,
        setFieldTouched,
        handleChange,
        handleBlur,
        handleSubmit,
        resetForm
      }) => {
        const handleReset = () => {
          setDecoded (null)
          setRequestError (null)
          resetForm ()
        }

        /*
          O código completo segue sozinho, sem botão — a mesma escolha dos apps.

          Um BR Code não é digitado: é colado inteiro. Pedir que se cole e depois se clique
          em "consultar" era um passo que só existia porque a tela não sabia dizer se o
          código estava completo. Sabendo — `isPixPayload` confere o CRC —, ela pode seguir
          assim que ele estiver inteiro.

          A espera de 300 ms é a mesma dos campos de busca do site: evita disparar a leitura
          no meio de uma colagem que chegue em pedaços, e dá tempo de quem colou o código
          errado ver o aviso antes de a tela trocar embaixo dele.
        */
        const scheduleDecode = value => {
          clearTimeout (decodeTimer.current)

          if (!BankingService.isPixPayload (value)) return

          decodeTimer.current = setTimeout (() => handleSubmit (), DECODE_DEBOUNCE_MS)
        }

        const handlePayloadChange = event => {
          handleChange ('payload') (event)
          setRequestError (null)
          scheduleDecode (event.target.value)
        }

        const handlePaste = async () => {
          try {
            const text = await navigator.clipboard.readText ()

            if (!text) return

            const payload = text.trim ()

            setFieldValue ('payload', payload)
            setFieldTouched ('payload', true)
            setDecoded (null)
            setRequestError (null)
            scheduleDecode (payload)
          } catch (error) {
            /*
              A leitura da área de transferência depende de permissão do navegador e de
              contexto seguro. Negada, não há o que avisar: o Ctrl+V continua funcionando no
              campo, que é como o código chega na maior parte das vezes.
            */
            console.log (error)
          }
        }

        const receiverName = decoded?.receiver?.tradingName ?? decoded?.receiver?.name
        const canPay = decoded?.canBePaidWithBalance !== false

        return (
          <Form onSubmit={handleSubmit}>
            <CardHeader>
              <CardTitle>Pix copia e cola</CardTitle>
              <MdQrCode2 size={18} color='#FF9800'/>
            </CardHeader>
            <Caption>
              {'Cole o código Pix copia e cola. Você confere quem vai receber antes de '
              + 'confirmar o pagamento'}
            </Caption>
            <Divider/>

            <Row>
              <FormField>
                <FieldLabel>Código Pix</FieldLabel>
                <TextInput
                  name='payload'
                  placeholder='00020126...'
                  value={values.payload}
                  disabled={Boolean (decoded)}
                  onChange={handlePayloadChange}
                  onBlur={handleBlur ('payload')}
                />
                {touched.payload && errors.payload && (
                  <WarningText>{errors.payload}</WarningText>
                )}
              </FormField>
            </Row>

            {requestError && <WarningText>{requestError}</WarningText>}

            {!decoded ? (
              <FormActions>
                <SecondaryButton
                  label='Colar'
                  onClick={handlePaste}
                  color='#606060'
                  width={110}
                />
                {/*
                  Sem botão de consultar: o que aparece aqui é o estado da leitura, para a
                  tela nunca ficar parada em silêncio enquanto decide sozinha.
                */}
                <Caption>{renderCodeHint (values.payload, isSubmitting)}</Caption>
              </FormActions>
            ) : (
              <>
                <Divider/>
                <DataList>
                  <DataRow>
                    <DataLabel>Quem recebe</DataLabel>
                    <DataValue>{receiverName ?? 'Não informado'}</DataValue>
                  </DataRow>
                  {decoded.receiver?.cpfCnpj && (
                    <DataRow>
                      <DataLabel>CPF/CNPJ</DataLabel>
                      <DataValue>{decoded.receiver.cpfCnpj}</DataValue>
                    </DataRow>
                  )}
                  {decoded.receiver?.ispbName && (
                    <DataRow>
                      <DataLabel>Instituição</DataLabel>
                      <DataValue>{decoded.receiver.ispbName}</DataValue>
                    </DataRow>
                  )}
                  {decoded.dueDate && (
                    <DataRow>
                      <DataLabel>Vencimento</DataLabel>
                      <DataValue>{formatDate (decoded.dueDate)}</DataValue>
                    </DataRow>
                  )}
                  <DataRow>
                    <DataLabel>Valor do Pix</DataLabel>
                    <MoneyTextMask value={Number (decoded.value ?? 0)}/>
                  </DataRow>
                  {Number (decoded.discount) > 0 && (
                    <DataRow>
                      <DataLabel>Desconto aplicado</DataLabel>
                      <MoneyTextMask value={Number (decoded.discount)}/>
                    </DataRow>
                  )}
                  {Number (decoded.interest) > 0 && (
                    <DataRow>
                      <DataLabel>Juros aplicados</DataLabel>
                      <MoneyTextMask value={Number (decoded.interest)}/>
                    </DataRow>
                  )}
                  {Number (decoded.fine) > 0 && (
                    <DataRow>
                      <DataLabel>Multa aplicada</DataLabel>
                      <MoneyTextMask value={Number (decoded.fine)}/>
                    </DataRow>
                  )}
                  <DataRow>
                    <DataLabel>Tarifa do Pix</DataLabel>
                    <MoneyTextMask value={Number (decoded.fee ?? 0)}/>
                  </DataRow>
                </DataList>

                {/*
                  Só um código que admite outro valor mostra o campo. Nos de valor fixo o
                  titular não escolhe, e um campo editável ali prometeria o que o arranjo do
                  Pix não cumpre.
                */}
                {canChangeValue && (
                  <Row>
                    <FormField>
                      <FieldLabel>Valor a pagar</FieldLabel>
                      <MoneyInput
                        name='value'
                        placeholder='R$ 0,00'
                        value={values.value}
                        maxValue={availableBalance}
                        onValueChange={setFieldValue}
                        onBlur={handleBlur ('value')}
                      />
                      {touched.value && errors.value && (
                        <WarningText>{errors.value}</WarningText>
                      )}
                    </FormField>
                  </Row>
                )}

                <HighlightRow>
                  <DataLabel><b>Total a pagar</b></DataLabel>
                  <PlainMoneyText value={
                    canChangeValue && values.value > 0
                      ? Number (values.value) + Number (decoded.fee ?? 0)
                      : total
                  }/>
                </HighlightRow>

                <Row>
                  <FormField>
                    <FieldLabel>Agendar para (opcional)</FieldLabel>
                    <DateInput
                      placeholder='Pagar agora'
                      value={values.scheduleDate}
                      clearable
                      disabledDates={isPastDate}
                      onChange={date => setFieldValue ('scheduleDate', date)}
                    />
                  </FormField>
                  <FormField stretch={2}>
                    <FieldLabel>Descrição (opcional)</FieldLabel>
                    <TextInput
                      name='description'
                      placeholder='Aparece no seu extrato'
                      value={values.description}
                      onChange={handleChange ('description')}
                      onBlur={handleBlur ('description')}
                    />
                    {touched.description && errors.description && (
                      <WarningText>{errors.description}</WarningText>
                    )}
                  </FormField>
                </Row>

                {!canPay && (
                  <WarningText>
                    {'O total a pagar é maior que o seu saldo disponível'}
                  </WarningText>
                )}

                <FormActions>
                  <SecondaryButton
                    label='Consultar outro'
                    onClick={handleReset}
                    disabled={paying}
                    color='#606060'
                    width={160}
                  />
                  <MainButton
                    label={values.scheduleDate ? 'Agendar pagamento' : 'Pagar'}
                    onClick={toggleConfirmDialog}
                    loading={paying}
                    disabled={!canPay}
                    width={values.scheduleDate ? 180 : 150}
                  />
                </FormActions>
              </>
            )}

            {receipt && (
              <>
                <Divider/>
                <Row style={{justifyContent: 'space-between', alignItems: 'center'}}>
                  <InfoText>
                    {`Último Pix${receipt.receiverName ? ` para ${receipt.receiverName}` : ''}`}
                  </InfoText>
                  <StatusBadge color={BankingService.getTransactionStatusColor (receipt.status)}>
                    {BankingService.getTransactionStatusLabel (receipt.status)}
                  </StatusBadge>
                </Row>
              </>
            )}

            <AlertDialog
              visible={showConfirmDialog}
              title={values.scheduleDate ? 'Confirmar agendamento' : 'Confirmar Pix'}
              message={
                `${values.scheduleDate ? 'Agendar o Pix de' : 'Pagar'} `
                + `${formatMoney (canChangeValue && values.value > 0
                  ? values.value
                  : decoded?.totalValue ?? decoded?.value ?? 0)} `
                + `para ${receiverName ?? 'o favorecido deste código'}`
                + `${values.scheduleDate ? ` em ${formatDate (values.scheduleDate)}` : ''}?`
              }
              confirmText={values.scheduleDate ? 'Agendar' : 'Pagar'}
              cancelText='Cancelar'
              /* Ver `PixForm`: fechar aqui reabria o diálogo em vez de fechá-lo. */
              onConfirm={() => payQrCode (values, resetForm)}
              onClose={toggleConfirmDialog}
            />
            <AlertDialog
              visible={showOkDialog}
              title={receipt?.scheduledAt ? 'Pix agendado' : 'Pix enviado'}
              message={
                receipt?.scheduledAt
                  ? 'O Pix será enviado na data agendada e o saldo debitado no dia'
                  : 'O Pix foi enviado e o saldo já foi debitado'
              }
              confirmText='Ok'
              onClose={toggleOkDialog}
            />
          </Form>
        )
      }}
    </Formik>
  )
}
